import * as PIXI from "pixi.js";
import { queueIdleAfterLanding } from "../Shared/landIdleJoin";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID, JACKPOT_SKIN_RANK } from "../../SymbolData";
import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { COIN_LABEL_MAX_WIDTH_RATIO } from "../Shared/coinValueLabel";
import { queueMeshWarm } from "../Shared/spineMeshWarm";

/**
 * HoldAndWinOverlayCell — the live spine of one Hold & Win cell.
 *
 * When a cell lands on anything but a blank, the board hides the cell's
 * static picture and this plays the symbol's spine in its place: `in`, then
 * its idle, looping. The value rides INSIDE the spine, on its text slot, so it
 * moves with the landing.
 *
 *   coin (c)          in -> win -> idle   value on txt_slot_coin
 *   jackpot coin (cj) in -> win -> idle   NO value — the tier word is its face
 *   chest (ch skins)  in -> closed_idle   value on txt_slot_chest
 *                     (collected: open -> open_idle — see collect())
 *                     (End Collector: close -> closed_idle — see darken())
 *   scarab (scr)      in -> idle
 *                     (settlement: win + its "×N" popping in — revealScarab())
 *   jackpot coin hit by a scarab: double_value — the tier word shrinks away and
 *                     the multiplied number pops in, in the tier's colours
 *                     (revealJackpotValue())
 *
 * One Spine per symbol rig per cell, built the first time that rig lands here
 * and reused after — never rebuilt per spin.
 */

const IDLE_BY_RIG = { ch: "closed_idle" };
// The silver chest's skin (SymbolData 12) — it collects once, then shuts.
const SILVER_CHEST_SKIN = "silver";
// Blend into a spent silver chest's `close`, seconds.
const CLOSE_MIX = 0.15;
// ── The chest's COLLECT jiggle (2026-10-06) ── `collect` on its own track,
// over the chest's idle, once per arriving value; blended in and back out.
const COLLECT_TRACK = 1;
// The revealing scarab's lift over its row level's other cells (0–9).
// const REVEALING_Z = 5;
// // Above the other scarabs only (-2 + 1 = -1), still under every coin (0).
// const REVEALING_Z = 1;
const REVEALING_Z = 5;
// ── The cell a scarab is hitting NOW (2026-10-15: "which ever coin is hit at
// the moment must have higher z index than coins around … only scarab can") ──
// its row level + this, whatever it is: over every coin and chest (0-8),
// under every scarab (9, the working one 14). Back in its place after.
// const HIT_Z = 12;
const HIT_Z = 8.5;
const COLLECT_MIX_IN = 0.06;
const COLLECT_MIX_OUT = 0.1;
// ── The scarab's glow behind a multiplied coin / chest (2026-10-12) ──
// `multiply_glow`, hand-added to c / cj / ch.json (the scarab's core glow +
// fire ring, scaled to just past the coin, first in the draw order), on its
// own track over whatever the rig plays. It switches itself off at its end.
const MULTIPLY_GLOW = "multiply_glow";
const MULTIPLY_GLOW_TRACK = 2;
// // Blend from the end of `open` into `open_idle`, seconds (see collect()).
// const OPEN_IDLE_MIX = 0.2; (fixed in ch.json instead, 2026-10-07)
// The coin rigs — a landing plays `win` between `in` and the idle.
// const COIN_RIGS = new Set(["c", "cj"]); (landing is in -> idle now, 2026-10-07)
// `cj` prints nothing: the jackpot coin's face is its tier word (MINI …
// GRAND), and the value showed through behind it (Vladimir, 2026-09-29).
// Its `double_value` clip — the moment it turns into a value — is a later step.
// const TEXT_SLOT_BY_RIG = { c: "txt_slot_coin", cj: "txt_slot_coin", ch: "txt_slot_chest" };
const TEXT_SLOT_BY_RIG = { c: "txt_slot_coin", ch: "txt_slot_chest" };

// A jackpot coin's revealed number takes its tier word's colours (fontStyles
// coin_value_<tier>.json), keyed by the cj skin it landed with.
const JACKPOT_STYLE_BY_SKIN = {
  Jp_mini: "coinValueMini",
  Jp_minor: "coinValueMinor",
  Jp_midi: "coinValueMidi",
  Jp_major: "coinValueMajor",
  Jp_grand: "coinValueGrand",
};

// The scarab's "×N": the coin number's face, larger (a stand-in until the
// animator delivers the reveal clip with its own number — animator TODO).
// Exported: the scarab's flying multiplier is the same size (scarabReveal).
export const SCARAB_LABEL_SCALE = 1.4;
// Its pop-in, ms: grows past full size, then settles.
const SCARAB_POP_MS = 320;
const SCARAB_POP_OVERSHOOT = 1.25;
// The scarab's cell sorts above every other cell (see play()).
// const SCARAB_Z_INDEX = 100;
// ── On top of the cell's ROW level now (2026-10-01) ── HoldAndWinOverlay
// puts a row's cells under the lock plates (locked) or over them (open) —
// setBaseZ. The scarab is lifted within its level only, never across the
// plates (plates 10, open rows 20).
// const SCARAB_Z_INDEX = 5;
// ── By importance, inside the row level (2026-10-06) ── a coin never draws
// over a chest or a scarab: coin < jackpot coin < silver chest < golden chest
// < scarab. All under 10, so a locked row's cells stay under the plates (10).
// const Z_BY_IMPORTANCE = { c: 0, cj: 1, chest: 2, golden_chest: 3, scr: 4 };
// Jackpot coins by tier on top of that (JACKPOT_SKIN_RANK, Mini 1 … Grand 5,
// Royal 6): coin 0 < jackpot coins 1–6 < chest < golden chest < scarab,
// still under the plates' 10.
// const Z_BY_IMPORTANCE = { c: 0, cj: 0, chest: 7, golden_chest: 8, scr: 9 };
// // ── The scarab UNDER the coins now (2026-10-15: its glow "goes on top of
// // everything … blurs things … can it go behind coins") ── one spine, so the
// // whole scarab goes below every coin and chest; its glow then sits behind them.
// const Z_BY_IMPORTANCE = { c: 0, cj: 0, chest: 7, golden_chest: 8, scr: -2 };
// ── The hierarchy, always (2026-10-15: "scarab tallest, chest second, coins
// underneath, there is no way that scarab can be lower than coin") ── the
// scarab on top again (9 — under the lock plates' 10 in a locked row), over
// the cell being hit (HIT_Z 8.5).
const Z_BY_IMPORTANCE = { c: 0, cj: 0, chest: 7, golden_chest: 8, scr: 9 };
// The scarab spine's own multiplier slot (scr, 2026-10-01): the "×N" sits in
// it, so its bone places it and the scarab's clips carry it. A spine without
// it keeps the old placement (the label on the spine's centre).
const SCARAB_MULT_SLOT = "txt_slot_mplier";
// The scarab's `darken` (scr.json): its body down to 797979 over 1 s — its
// ×N label follows (darken).
const SCARAB_DARK_BRIGHTNESS = 0x79 / 255;
const SCARAB_DARKEN_MS = 1000;
// ── The DARKER darken (PO, 2026-11-03) ── `darken_max`, hand-built in c / cj /
// ch / scr.json from each one's own `darken` at 0.7 of its end colours: the
// symbols a max win leaves uncollected. The scarab's ×N follows at the same.
export const DARKEN_MAX_CLIP = "darken_max";
const DARKEN_MAX_RATIO = 0.7;
// A coin a scarab multiplies plays its ANTICIPATION clips, wait_in ->
// wait_out, quick (Vladimir, 2026-10-01: "its better looking"): each is 1 s,
// at this speed the pair takes the 1 s the `win` it replaces did.
const MULTIPLIED_TIME_SCALE = 2;
// ── Dimmed while a scarab works elsewhere (PO, 2026-10-09) ── the idles'
// `_dark` twins (hand-built in c / cj / ch.json: the idle at `darken`'s own
// colours), blended in and out over DIM_MIX, seconds. The value label takes
// the same brightness (it is a slot object: spine colours reach only its alpha).
const IDLE_DARK = { idle: "idle_dark", closed_idle: "closed_idle_dark", open_idle: "open_idle_dark" };
export const DIM_MIX = 0.25;
// const DIM_LABEL_BRIGHTNESS = 0.51;
// One step darker with the dark idles (x0.85, 2026-10-12).
const DIM_LABEL_BRIGHTNESS = 0.43;

export default class HoldAndWinOverlayCell {
  constructor(container) {
    this.container = container;
    this.cellHeight = 0;
    this.rigs = new Map(); // rig name -> { spine, text }
    this.active = null;
    // Resolves when the current landing has played out (see landed()).
    this._landed = Promise.resolve();
    this._settleLanded = null;
    // The skin the current rig landed with (a jackpot coin's tier).
    this._skin = null;
    // The scarab's "×N" (revealScarab), and its pop tween.
    this._multLabel = null;
    this._popTick = null;
    // setDimmed: on or off, and the label's tint tween.
    this._dimmed = false;
    this._dimTick = null;
  }

  /** The rig on this cell now — "c", "cj", "ch", "scr" — or null. */
  rig() {
    for (const [name, entry] of this.rigs) if (entry === this.active) return name;
    return null;
  }

  /** The skin the cell's symbol wears — "silver" / "gold" on a chest — or null. */
  skin() {
    return this._skin;
  }

  /**
   * The scarab's glow alone, for a hit that plays its own clip instead of
   * playMultiplied — a jackpot coin turning from its word (scarabReveal).
   * Call it AFTER that clip has started (revealJackpotValue clears tracks).
   */
  playMultiplyGlow() {
    this._playMultiplyGlow();
  }

  /**
   * The scarab's glow behind this rig (MULTIPLY_GLOW), once — a scarab has
   * just multiplied it. Its own track, so the rig's own clip is untouched; a
   * rig without the clip does nothing.
   */
  _playMultiplyGlow() {
    const spine = this.active?.spine;
    if (!spine?.state || !spine.skeleton?.data?.findAnimation(MULTIPLY_GLOW)) return;
    const entry = spine.state.setAnimation(MULTIPLY_GLOW_TRACK, MULTIPLY_GLOW, false);
    // Released at its end (it has already switched its art off by then).
    spine.state.addEmptyAnimation(MULTIPLY_GLOW_TRACK, 0, entry?.animation?.duration ?? 0);
  }

  /**
   * Play the rig's `win` once, then back to its idle. Resolves when `win`
   * completes (or is cut). Resolved at once for a rig without one (the chest).
   */
  playWin() {
    const spine = this.active?.spine;
    const data = spine?.skeleton?.data;
    if (!data?.findAnimation("win")) return Promise.resolve();
    this._endLanding();
    const rigName = this.rig();
    const idle = IDLE_BY_RIG[rigName] ?? "idle";
    spine.state.clearTracks();
    const winEntry = spine.state.setAnimation(0, "win", false);
    if (data.findAnimation(idle)) spine.state.addAnimation(0, idle, true, 0);
    return new Promise((resolve) => {
      winEntry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /**
   * A coin a scarab has just multiplied: its anticipation clips, wait_in ->
   * wait_out at MULTIPLIED_TIME_SCALE, then back to its idle. A rig without
   * them plays its `win` instead. Resolves when wait_out completes.
   */
  playMultiplied() {
    const spine = this.active?.spine;
    const data = spine?.skeleton?.data;
    // ── Its `win` (animator export 2026-10-07) ── the number pulse moved
    // INTO `win` (txt_slot_coin 1 -> 1.4 -> 1) on c and cj, and our
    // hand-built `multiply` is gone — `win` is the multiply now. (cj also has
    // wait clips now, so this must come before the wait-clip path below.)
    // if (data?.findAnimation("win")) return this.playWin();
    // ...with the scarab's glow behind it — after `win`, which clears tracks.
    if (data?.findAnimation("win")) {
      const win = this.playWin();
      this._playMultiplyGlow();
      return win;
    }
    // ── Its own `multiply` clip first (2026-10-06) ── the wait clips plus the
    // NUMBER pulsing up and back, so the player sees the value change.
    // Hand-built in c.json / cj.json for now; the animator's real one drops in
    // under the same name.
    if (data?.findAnimation("multiply")) {
      this._endLanding();
      const idle = IDLE_BY_RIG[this.rig()] ?? "idle";
      spine.state.clearTracks();
      const entry = spine.state.setAnimation(0, "multiply", false);
      if (data.findAnimation(idle)) spine.state.addAnimation(0, idle, true, 0);
      return new Promise((resolve) => {
        entry.listener = { complete: () => resolve(), end: () => resolve() };
      });
    }
    if (!data?.findAnimation("wait_in") || !data.findAnimation("wait_out")) {
      return this.playWin();
    }
    this._endLanding();
    const idle = IDLE_BY_RIG[this.rig()] ?? "idle";
    spine.state.clearTracks();
    const inEntry = spine.state.setAnimation(0, "wait_in", false);
    inEntry.timeScale = MULTIPLIED_TIME_SCALE;
    const outEntry = spine.state.addAnimation(0, "wait_out", false, 0);
    outEntry.timeScale = MULTIPLIED_TIME_SCALE;
    if (data.findAnimation(idle)) spine.state.addAnimation(0, idle, true, 0);
    return new Promise((resolve) => {
      outEntry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /**
   * A scarab reveals (GDD "Scarab"): its `win`, with its multiplier popping in
   * over it as "×N" — and staying there. Resolves when `win` completes.
   */
  revealScarab(multiplier) {
    const spine = this.active?.spine;
    if (!spine || this.rig() !== "scr") return Promise.resolve();
    // if (!this._multLabel) this._multLabel = createCoinLabel();
    // const label = this._multLabel;
    // if (label.parent !== spine) spine.addChild(label);
    const label = this._attachMultLabel(spine);
    setCoinLabel(
      label,
      // `×${multiplier}`,
      // The GDD's form: "2×, 3×, 4×, 5×, 10× or 20×".
      `${multiplier}×`,
      Settings.SYMBOL_HEIGHT *
        Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER *
        SCARAB_LABEL_SCALE,
      Settings.SYMBOL_HEIGHT * 0.9,
    );
    this._pop(label);
    return this.playWin();
  }

  /** The scarab's "×N" shown at once, no pop and no `win` (a skip). */
  showMultiplier(multiplier) {
    const spine = this.active?.spine;
    if (!spine || this.rig() !== "scr") return;
    // const text = `×${multiplier}`;
    const text = `${multiplier}×`; // the GDD's form, as revealScarab
    if (this._multLabel?._label === text) return;
    // if (!this._multLabel) this._multLabel = createCoinLabel();
    // if (this._multLabel.parent !== spine) spine.addChild(this._multLabel);
    this._attachMultLabel(spine);
    setCoinLabel(
      this._multLabel,
      text,
      Settings.SYMBOL_HEIGHT *
        Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER *
        SCARAB_LABEL_SCALE,
      Settings.SYMBOL_HEIGHT * 0.9,
    );
  }

  /**
   * A jackpot coin a scarab has multiplied: `double_value` — its tier word
   * shrinks away and `value` pops in on txt_slot_coin, in the tier's colours.
   * Resolves when the clip completes. No-op on anything but a jackpot coin.
   */
  // `withWin` false (the collections, 2026-10-01): resolve when the value is
  // up — no extra `win` beat before the coin is collected.
  revealJackpotValue(value, { withWin = true } = {}) {
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("cj")) return Promise.resolve();
    this._jackpotRevealed = true;
    const { spine } = entry;
    if (!entry.text && spine.skeleton.findSlot("txt_slot_coin")) {
      entry.text = createCoinLabel();
      // spine.addSlotObject("txt_slot_coin", entry.text);
      // In a holder, so the squeeze survives (see _rig).
      const holder = new PIXI.Container();
      holder.name = "coin_value_holder";
      holder.addChild(entry.text);
      spine.addSlotObject("txt_slot_coin", holder);
    }
    entry.styleName = JACKPOT_STYLE_BY_SKIN[this._skin] ?? "coinValue";
    this._setValue(entry, value);
    const data = spine.skeleton.data;
    if (!data.findAnimation("double_value")) return Promise.resolve();
    this._endLanding();
    spine.state.clearTracks();
    const revealEntry = spine.state.setAnimation(0, "double_value", false);
    // Then its `win` once, like every other coin a scarab hits — the player
    // sees that something happened to it (Vladimir, 2026-09-29).
    let lastEntry = revealEntry;
    if (withWin && data.findAnimation("win")) {
      lastEntry = spine.state.addAnimation(0, "win", false, 0);
    }
    if (data.findAnimation("idle")) spine.state.addAnimation(0, "idle", true, 0);
    // ── The tier word goes for GOOD ──
    // double_value shrinks it (bone coin_jckpt -> scale 0), but the clips after
    // it do not key that bone, so it springs back to full size the moment
    // double_value ends — word and number on the coin at once (checked frame
    // by frame with spine-core). So once it has shrunk, its attachment comes
    // off: no clip keys coin_jackpot's attachment, and play() puts the skin
    // back on the next landing.
    const dropWord = () => spine.skeleton.findSlot("coin_jackpot")?.setAttachment(null);
    return new Promise((resolve) => {
      if (lastEntry === revealEntry) {
        revealEntry.listener = {
          complete: () => {
            dropWord();
            resolve();
          },
          end: () => resolve(),
        };
      } else {
        revealEntry.listener = { complete: dropWord };
        lastEntry.listener = { complete: () => resolve(), end: () => resolve() };
      }
    });
  }

  /**
   * A jackpot coin still showing its tier word — not yet turned into its value
   * (by a scarab, or an earlier collection). See revealHiddenJackpots.
   */
  /** The FONT_STYLES key its number is drawn in — a turned jackpot coin's tier. */
  valueStyle() {
    return this.active?.styleName || "coinValue";
  }

  isHiddenJackpot() {
    return !!this.active && this.active === this.rigs.get("cj") && !this._jackpotRevealed;
  }

  /** Grow `sprite` in past full size and settle it, on the ticker. */
  _pop(sprite) {
    if (this._popTick) PIXI.Ticker.shared.remove(this._popTick);
    const base = sprite.scale.x || 1;
    let elapsed = 0;
    const tick = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(1, elapsed / SCARAB_POP_MS);
      // 0 -> overshoot over the first 60%, overshoot -> 1 after.
      const k =
        t < 0.6
          ? SCARAB_POP_OVERSHOOT * (1 - Math.pow(1 - t / 0.6, 2))
          : SCARAB_POP_OVERSHOOT + (1 - SCARAB_POP_OVERSHOOT) * ((t - 0.6) / 0.4);
      sprite.scale.set(base * k, base * k);
      if (t >= 1) {
        PIXI.Ticker.shared.remove(tick);
        if (this._popTick === tick) this._popTick = null;
      }
    };
    this._popTick = tick;
    sprite.scale.set(0);
    PIXI.Ticker.shared.add(tick);
  }

  /**
   * The "×N" label, worn where this scarab spine wants it: in its own
   * `txt_slot_mplier` (inside a holder — spine-pixi rewrites a slot object's
   * transform every frame, and the pop scales the label itself), or, on a
   * spine without that slot, on the spine's centre as before.
   */
  _attachMultLabel(spine) {
    if (!this._multLabel) this._multLabel = createCoinLabel();
    const label = this._multLabel;
    // A new ×N starts bright (a darkened scarab's left it dim).
    if (this._multTick) PIXI.Ticker.shared.remove(this._multTick);
    this._multTick = null;
    label.tint = 0xffffff;
    if (spine.skeleton?.findSlot(SCARAB_MULT_SLOT)) {
      if (!this._multHolder) {
        this._multHolder = new PIXI.Container();
        this._multHolder.name = "scarab_multiplier";
      }
      if (label.parent !== this._multHolder) this._multHolder.addChild(label);
      if (this._multHolderSpine !== spine) {
        spine.addSlotObject(SCARAB_MULT_SLOT, this._multHolder);
        this._multHolderSpine = spine;
      }
    } else if (label.parent !== spine) {
      spine.addChild(label);
    }
    return label;
  }

  _hideMultiplier() {
    if (this._popTick) PIXI.Ticker.shared.remove(this._popTick);
    this._popTick = null;
    if (this._multLabel) setCoinLabel(this._multLabel, "", 0);
  }

  /**
   * Resolves when this cell's landing is OVER — its `in` (and a coin's `win`)
   * has played and the idle has started. Already resolved for a cell that is
   * not landing. The chests' collection waits on this: a chest collecting
   * before its own `in` finished was barely seen landing (Vladimir,
   * 2026-09-29: "let the land animation finish properly then start").
   */
  landed() {
    return this._landed;
  }

  _endLanding() {
    const settle = this._settleLanded;
    this._settleLanded = null;
    settle?.();
  }

  setCellHeight(height) {
    this.cellHeight = height;
    for (const [rig, entry] of this.rigs) this._scale(entry.spine, rig);
  }

  /** Play `symbolId` landing here, printing `value` if the rig has a slot. */
  play(symbolId, value = null, skinOverride = null) {
    const info = symbolData[symbolId];
    const rigName = info?.spine;
    // A blank is an empty cell — no spine, ever (its `bl` rig is the old
    // Mad Labs panel and must not be drawn).
    if (!rigName || symbolId === BLANK_SYMBOL_ID) {
      this.clear();
      return false;
    }

    const entry = this._rig(rigName);
    if (!entry) return false;

    if (this.active && this.active !== entry) this._hide(this.active);
    this.active = entry;
    // The scarab draws above every other symbol — a coin's landing next to it
    // must never cover it (Vladimir, 2026-09-30). Below the lock bars (500)
    // and the trails (1000) of HoldAndWinOverlay.
    // this.container.zIndex = rigName === "scr" ? SCARAB_Z_INDEX : 0;
    // this._isScarab = rigName === "scr";
    this._isScarab = rigName === "scr";
    this._importanceZ =
      rigName === "ch"
        ? Z_BY_IMPORTANCE[(skinOverride || info.skin) === "gold" ? "golden_chest" : "chest"]
        : (Z_BY_IMPORTANCE[rigName] ?? 0) +
          (rigName === "cj" ? JACKPOT_SKIN_RANK[skinOverride || info.skin] ?? 1 : 0);
    this._applyZ();

    const { spine } = entry;
    this._currentSymbolPay = info.symbolPay;
    this._scale(spine, rigName);
    const skin = skinOverride || info.skin;
    if (skin && spine.skeleton.data.findSkin(skin)) {
      spine.skeleton.setSkinByName(skin);
      spine.skeleton.setSlotsToSetupPose();
    }
    this._skin = skin || null;
    // A jackpot coin lands on its tier word; revealJackpotValue turns it.
    this._jackpotRevealed = false;
    this._hideMultiplier();
    // this._setValue(entry, value);
    // A jackpot coin lands on its tier word — never a number, even when a
    // previous visit's reveal left a label on its slot.
    if (rigName === "cj") entry.styleName = null;
    this._setValue(entry, rigName === "cj" ? 0 : value);

    // A chest that already holds a value (collected earlier, or restored)
    // rests OPEN; an empty one rests closed.
    // const idle =
    //   rigName === "ch" && value > 0
    //     ? "open_idle"
    //     : IDLE_BY_RIG[rigName] ?? "idle";
    // ...except a SILVER chest: it collects once, so holding a value means it
    // is spent — it rests CLOSED (closeIfSpent). Only the golden one stays
    // open (2026-10-06).
    const idle =
      rigName === "ch" && value > 0 && this._skin !== SILVER_CHEST_SKIN
        ? "open_idle"
        : IDLE_BY_RIG[rigName] ?? "idle";
    const data = spine.skeleton.data;
    // A landing still owed from before is over now — this one replaces it.
    this._endLanding();
    spine.state.clearTracks();
    if (data.findAnimation("in")) {
      const inEntry = spine.state.setAnimation(0, "in", false);
      let lastEntry = inEntry;
      // A COIN lands with a flourish: in -> win -> idle (Vladimir,
      // 2026-09-29). Chests and scarabs go straight to their idle.
      // if (COIN_RIGS.has(rigName) && data.findAnimation("win")) {
      //   lastEntry = spine.state.addAnimation(0, "win", false, 0);
      // }
      // ── Just in -> idle now (Vladimir, 2026-10-07) ── `win` carries the
      // number pulse a scarab's multiply plays (animator export); on a
      // landing it is not wanted.
      let idleEntry = null;
      if (data.findAnimation(idle)) {
        // idleEntry = spine.state.addAnimation(0, idle, true, 0);
        // From the landing's end pose — one movement (Games/Shared/landIdleJoin).
        idleEntry = queueIdleAfterLanding(spine, idle);
      }
      // The landing is over when the idle STARTS — or, with no idle, when the
      // last landing clip completes. The spine's own event, never a timer.
      this._landed = new Promise((resolve) => {
        this._settleLanded = resolve;
      });
      const end = () => this._endLanding();
      if (idleEntry) idleEntry.listener = { start: end };
      else lastEntry.listener = { complete: end };
    } else if (data.findAnimation(idle)) {
      spine.state.setAnimation(0, idle, true);
    }
    // First frame now, so the swap from the static picture cannot flash.
    spine.state.update(0);
    spine.state.apply(spine.skeleton);
    spine.visible = true;
    spine.autoUpdate = true;
    return true;
  }

  /**
   * A chest collects (GDD "Chest" / "Golden Chest"): `open`, then `open_idle`,
   * and its value becomes the server's — ONCE. A chest already open (a golden
   * chest recollecting) only takes its new number and keeps playing
   * `open_idle`. `instant` (a skip) goes straight to `open_idle` with the value.
   * No-op on anything but a chest.
   *
   * Returns a promise that resolves when `open` has PLAYED OUT (the moment
   * `open_idle` starts) — the collection fires its trails only then, into a
   * chest that is already open (Vladimir, 2026-09-29: "use animations proper
   * and real timings and events"). Resolved at once for a skip or a non-chest.
   */
  collect(value, instant = false) {
    this._endLanding();
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("ch")) return Promise.resolve();
    const { spine } = entry;
    this._setValue(entry, value);
    // ── It opens ONCE ──
    // A chest that is already open (a golden chest recollecting, or any chest
    // collecting again) stays on what it is playing — only its number changes.
    // open -> open_idle happens a single time, and nothing more until the End
    // Collector shuts it (Vladimir, 2026-09-29).
    const playing = spine.state.getCurrent(0)?.animation?.name;
    if (playing === "open" || playing === "open_idle") return Promise.resolve();
    const data = spine.skeleton.data;
    spine.state.clearTracks();
    let opened = Promise.resolve();
    if (!instant && data.findAnimation("open")) {
      const openEntry = spine.state.setAnimation(0, "open", false);
      let idleEntry = null;
      if (data.findAnimation("open_idle")) {
        idleEntry = spine.state.addAnimation(0, "open_idle", true, 0);
        // `open` now ends with the lid held where `close` starts (65.07),
        // above `open_idle`'s first pose (54.42) — blended, so it does not
        // snap (animator export 2026-10-07).
        // if (idleEntry) idleEntry.mixDuration = OPEN_IDLE_MIX;
        // ── Fixed in ch.json instead (2026-10-07) ── `open` runs the lid back
        // to `open_idle`'s pose by its end again, so no blend is needed.
      }
      opened = new Promise((resolve) => {
        if (idleEntry) idleEntry.listener = { start: () => resolve() };
        else openEntry.listener = { complete: () => resolve() };
      });
    } else if (data.findAnimation("open_idle")) {
      spine.state.setAnimation(0, "open_idle", true);
    }
    spine.state.update(0);
    spine.state.apply(spine.skeleton);
    return opened;
  }

  /**
   * A value ARRIVES in this chest (a collection trail landed): the chest
   * jiggles as if money dropped in — its `collect` clip, on its own track OVER
   * whatever it is playing (open_idle), so the idle and its rays run on
   * underneath. Restarted by each arrival; blends back out after the last.
   * `collect` is hand-built in ch.json for now (lid bump + squash); the
   * animator's real one drops in under the same name. No-op without it.
   */
  playCollect() {
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("ch")) return;
    const { spine } = entry;
    // if (!spine.skeleton.data.findAnimation("collect")) return;
    // const collect = spine.state.setAnimation(COLLECT_TRACK, "collect", false);
    // ── The animator's `open_hit` (2026-10-07) ── replaces our hand-built
    // `collect`: lid jump, squash, ring burst, number pulse.
    const clip = this._chestHitClip();
    if (!clip) return;
    const collect = spine.state.setAnimation(COLLECT_TRACK, clip, false);
    if (collect) collect.mixDuration = COLLECT_MIX_IN;
    spine.state.addEmptyAnimation(COLLECT_TRACK, COLLECT_MIX_OUT, 0);
  }

  /**
   * The chest's hit clip for the state it is in (animator, 2026-10-07):
   * `open_hit` on an open chest (keys the lid), `closed_hit` on a closed one
   * (never touches the lid). Our hand-built `collect` / `multiply` are the
   * fallback for an older ch.json. Null when the rig has none.
   */
  _chestHitClip() {
    const spine = this.active?.spine;
    const data = spine?.skeleton?.data;
    if (!data) return null;
    const playing = spine.state.getCurrent(0)?.animation?.name;
    const open = playing === "open" || playing === "open_idle";
    const clip = open ? "open_hit" : "closed_hit";
    if (data.findAnimation(clip)) return clip;
    const legacy = open ? "collect" : "multiply";
    if (data.findAnimation(legacy)) return legacy;
    return data.findAnimation("multiply") ? "multiply" : null;
  }

  /**
   * A scarab multiplied this chest (GDD: scarabs multiply "Coins and Chests
   * with values"): its `multiply` clip on its OWN track, over whatever it is
   * doing — the lid is never keyed, so a closed chest stays closed and an open
   * one stays open (2026-10-06). The body bumps and the number pulses.
   * Hand-built in ch.json for now; the animator's real one drops in under the
   * same name. Resolves when the clip completes (at once without it).
   */
  playChestMultiplied() {
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("ch")) return Promise.resolve();
    const { spine } = entry;
    // if (!spine.skeleton.data.findAnimation("multiply")) return Promise.resolve();
    // const multiply = spine.state.setAnimation(COLLECT_TRACK, "multiply", false);
    // ── The animator's hit clips (2026-10-07) ── `open_hit` / `closed_hit`
    // by the lid's state (every chest is closed by the scarabs, so normally
    // `closed_hit`).
    // The scarab's glow behind it, as behind a multiplied coin (2026-10-12) —
    // whether or not the chest has a hit clip of its own.
    this._playMultiplyGlow();
    const clip = this._chestHitClip();
    if (!clip) return Promise.resolve();
    const multiply = spine.state.setAnimation(COLLECT_TRACK, clip, false);
    if (multiply) multiply.mixDuration = COLLECT_MIX_IN;
    spine.state.addEmptyAnimation(COLLECT_TRACK, COLLECT_MIX_OUT, 0);
    return new Promise((resolve) => {
      if (!multiply) return resolve();
      multiply.listener = { complete: () => resolve(), end: () => resolve() };
      return undefined;
    });
  }

  /**
   * A SILVER chest has made its one collection (GDD: it "collects once"):
   * the lid comes down — `close`, then `closed_idle` — so the player does not
   * wait for more from it (Vladimir, 2026-10-06). The End Collector later
   * darkens it like any collected cell. A golden chest recollects every
   * respin and stays open until its last collection; anything else is a
   * no-op.
   */
  // closeIfSpent() {
  //   const entry = this.active;
  //   if (!entry || entry !== this.rigs.get("ch") || this._skin !== SILVER_CHEST_SKIN) return;
  // ── A chest that has FINISHED ITS PURPOSE shuts (Vladimir, 2026-10-06) ──
  // The silver one after its one collection; the golden one after its LAST —
  // the respin that settles the round (`final`), before the scarabs.
  closeIfSpent(final = false) {
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("ch")) return;
    if (this._skin !== SILVER_CHEST_SKIN && !final) return;
    const { spine } = entry;
    const playing = spine.state.getCurrent(0)?.animation?.name;
    if (playing !== "open" && playing !== "open_idle") return;
    const data = spine.skeleton.data;
    if (!data.findAnimation("close")) return;
    const closeEntry = spine.state.setAnimation(0, "close", false);
    // `close` starts from a lid pose `open_idle` never reaches (animator
    // item 24) — blended in, not cut, so the lid does not jump.
    if (closeEntry) closeEntry.mixDuration = CLOSE_MIX;
    if (data.findAnimation("closed_idle")) {
      spine.state.addAnimation(0, "closed_idle", true, 0);
    }
  }

  /**
   * Resolves when the idle the cell is looping now reaches the end of its
   * current pass (`open_idle` on an open chest). The pause after a chest has
   * collected is this, not a number. Resolved at once with nothing looping.
   */
  idleLoopEnd() {
    const current = this.active?.spine?.state?.getCurrent?.(0);
    if (!current || !current.loop) return Promise.resolve();
    return new Promise((resolve) => {
      const previous = current.listener;
      current.listener = {
        ...previous,
        complete: (e) => {
          previous?.complete?.(e);
          current.listener = previous;
          resolve();
        },
      };
    });
  }

  /**
   * Collected by the End Collector: the rig's `darken` clip (the coin has it —
   * it dims the value), held on its last frame (GDD: "dim its value to
   * indicate completion"). The chest has none and closes instead (below).
   */
  // darken() {
  // `max`: the darker `darken_max` (a max win's uncollected symbols), `darken`
  // where a rig has none.
  darken({ max = false } = {}) {
    this._resetDim();
    const spine = this.active?.spine;
    const clip =
      max && spine?.skeleton.data.findAnimation(DARKEN_MAX_CLIP) ? DARKEN_MAX_CLIP : "darken";
    // ── The chest has no `darken`: it SHUTS ──
    //
    // Its "collected" is the lid coming down — `close`, then `closed_idle`
    // looping — with its value already off the slot (the End Collector sets it
    // to 0 first). Vladimir, 2026-09-29: it "needs something to tell us that we
    // have collected that value".
    // ── ...it DARKENS now, like a coin (2026-10-05, client: everything with a
    // value that does something in Hold & Win goes dark once collected) ──
    // `darken` hand-built in ch.json from the coin's own; `close` stays as the
    // fallback for a chest rig without it.
    if (spine && this.active === this.rigs.get("ch") && spine.skeleton.data.findAnimation("darken")) {
      spine.state.clearTracks();
      // spine.state.setAnimation(0, "darken", false);
      spine.state.setAnimation(0, clip, false);
      return;
    }
    if (spine && this.active === this.rigs.get("ch")) {
      const data = spine.skeleton.data;
      if (!data.findAnimation("close")) return;
      spine.state.clearTracks();
      spine.state.setAnimation(0, "close", false);
      if (data.findAnimation("closed_idle")) {
        spine.state.addAnimation(0, "closed_idle", true, 0);
      }
      return;
    }
    if (!spine?.skeleton.data.findAnimation("darken")) return;
    spine.state.clearTracks();
    // spine.state.setAnimation(0, "darken", false);
    spine.state.setAnimation(0, clip, false);
    // ── The scarab's ×N goes dark with it (2026-10-15) ── a label on its
    // slot, which no clip can colour: tinted down to the body's `darken`
    // (797979) over the clip's own second.
    if (this.active === this.rigs.get("scr") && this._multLabel) {
      // this._tintTo(this._multLabel, SCARAB_DARK_BRIGHTNESS, SCARAB_DARKEN_MS);
      const brightness =
        clip === DARKEN_MAX_CLIP ? SCARAB_DARK_BRIGHTNESS * DARKEN_MAX_RATIO : SCARAB_DARK_BRIGHTNESS;
      this._tintTo(this._multLabel, brightness, SCARAB_DARKEN_MS);
    }
  }

  /** A label's brightness tweened to `to` over `ms`, on the ticker. */
  _tintTo(label, to, ms) {
    if (this._multTick) PIXI.Ticker.shared.remove(this._multTick);
    const from = ((label.tint ?? 0xffffff) & 0xff) / 255;
    let elapsed = 0;
    const tick = () => {
      if (label.destroyed) {
        PIXI.Ticker.shared.remove(tick);
        return;
      }
      elapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(1, elapsed / ms);
      const v = Math.round((from + (to - from) * t) * 255);
      label.tint = (v << 16) | (v << 8) | v;
      if (t >= 1) {
        PIXI.Ticker.shared.remove(tick);
        if (this._multTick === tick) this._multTick = null;
      }
    };
    this._multTick = tick;
    PIXI.Ticker.shared.add(tick);
  }

  /** The landed spine's value sprite (its on-screen size is read off it). */
  valueLabel() {
    return this.active?.text ?? null;
  }

  /** Rewrite the value on the landed spine's text slot (a count-up). */
  setValue(value) {
    if (this.active) this._setValue(this.active, value);
  }

  /** Take the spine off this cell (the next spin, or a blank). */
  clear() {
    this._endLanding();
    this._hideMultiplier();
    this._resetDim();
    if (this.active) this._hide(this.active);
    this.active = null;
    // this.container.zIndex = 0;
    this._isScarab = false;
    this._importanceZ = 0;
    this._applyZ();
  }

  /**
   * The cell's ROW level in HoldAndWinOverlay — under the lock plates while
   * its row is locked, over them once open (setOpenRows).
   */
  setBaseZ(z) {
    this._baseZ = z;
    this._applyZ();
  }

  _applyZ() {
    // this.container.zIndex = (this._baseZ ?? 0) + (this._isScarab ? SCARAB_Z_INDEX : 0);
    // this.container.zIndex = (this._baseZ ?? 0) + (this._importanceZ ?? 0);
    // + the REVEALING scarab's lift (setRevealing): above every other cell of
    // its row level, the other scarabs included — still under the plates'
    // level above it (open rows sit at 20, the next level up is none).
    // this.container.zIndex =
    //   (this._baseZ ?? 0) + (this._importanceZ ?? 0) + (this._revealing ? REVEALING_Z : 0);
    // + the cell being hit (setHit): its own level, over everything but the
    // working scarab.
    this.container.zIndex = this._hit
      ? (this._baseZ ?? 0) + HIT_Z
      : (this._baseZ ?? 0) + (this._importanceZ ?? 0) + (this._revealing ? REVEALING_Z : 0);
  }

  /** A scarab is hitting this cell NOW — see HIT_Z. Off when its hit ends. */
  setHit(on) {
    this._hit = !!on;
    this._applyZ();
  }

  /**
   * The scarab revealing NOW draws above everything it multiplies, other
   * scarabs included (2026-10-06) — they all share one depth, so a neighbour
   * scarab later in the draw order sat on top of its glow. Off again once it
   * has gone dark.
   */
  setRevealing(on) {
    this._revealing = !!on;
    this._applyZ();
  }

  /**
   * Dim (or undim) the cell while a scarab multiplies OTHER cells: its idle
   * blends into the idle's `_dark` twin, keeping the loop's phase, and the
   * value label dims with it. Only from an idle loop — a cell mid-clip (its
   * landing, a multiply) is left alone. Returns true when the cell changed.
   */
  setDimmed(on) {
    const dim = !!on;
    if (this._dimmed === dim) return false;
    const spine = this.active?.spine;
    const current = spine?.state?.getCurrent(0);
    const playing = current?.animation?.name;
    if (dim) {
      const dark = IDLE_DARK[playing];
      if (!dark || !spine.skeleton.data.findAnimation(dark)) return false;
      this._switchLoop(spine, current, dark);
    } else {
      // Back to the idle — only if the cell is still on a dark one.
      const idle = Object.keys(IDLE_DARK).find((name) => IDLE_DARK[name] === playing);
      if (idle) this._switchLoop(spine, current, idle);
    }
    this._dimmed = dim;
    this._tintLabel(dim ? DIM_LABEL_BRIGHTNESS : 1);
    return true;
  }

  /** Blend into another loop at the same point of its cycle. */
  _switchLoop(spine, current, name) {
    const entry = spine.state.setAnimation(0, name, true);
    entry.mixDuration = DIM_MIX;
    entry.trackTime = current.trackTime;
  }

  /** The value label's brightness, blended over DIM_MIX on the ticker. */
  _tintLabel(to) {
    if (this._dimTick) PIXI.Ticker.shared.remove(this._dimTick);
    this._dimTick = null;
    const label = this.active?.text;
    if (!label) return;
    const brightness = (tint) => (tint & 0xff) / 255;
    const from = brightness(label.tint ?? 0xffffff);
    const set = (b) => {
      const v = Math.round(b * 255);
      label.tint = (v << 16) | (v << 8) | v;
    };
    let elapsed = 0;
    const tick = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(1, elapsed / (DIM_MIX * 1000));
      set(from + (to - from) * t);
      if (t >= 1) {
        PIXI.Ticker.shared.remove(tick);
        if (this._dimTick === tick) this._dimTick = null;
      }
    };
    this._dimTick = tick;
    PIXI.Ticker.shared.add(tick);
  }

  /** Back to full brightness at once (a new spin, a darken). */
  _resetDim() {
    if (this._dimTick) PIXI.Ticker.shared.remove(this._dimTick);
    this._dimTick = null;
    this._dimmed = false;
    if (this.active?.text) this.active.text.tint = 0xffffff;
  }

  /**
   * Build these rigs NOW, hidden — not on their first landing in this cell,
   * mid-respin (2026-10-07). `_rig` makes a hidden, non-updating spine and its
   * value label; nothing is shown and the active rig is untouched. Each one's
   * clip meshes then follow, a few spines a frame (Games/Shared/spineMeshWarm),
   * skipped while it is on screen.
   */
  prebuild(rigNames) {
    if (this._destroyed) return;
    for (const name of rigNames) {
      const entry = this._rig(name);
      const spine = entry?.spine;
      if (spine) queueMeshWarm({ spine, isBusy: () => spine.destroyed || spine.visible });
    }
    if (this.cellHeight) for (const [rig, entry] of this.rigs) this._scale(entry.spine, rig);
  }

  destroy() {
    this._destroyed = true;
    this._endLanding();
    this._hideMultiplier();
    this._resetDim();
    for (const { spine } of this.rigs.values()) spine.destroy({ children: true });
    this.rigs.clear();
    this.active = null;
  }

  // ── internals ──

  _rig(rigName) {
    if (this.rigs.has(rigName)) return this.rigs.get(rigName);
    const skeleton = `${rigName}-skeleton`;
    const atlas = `${rigName}-atlas`;
    if (!PIXI.Assets.cache.has(skeleton) || !PIXI.Assets.cache.has(atlas)) {
      return null;
    }
    const spine = Spine.from({ skeleton, atlas });
    spine.visible = false;
    spine.autoUpdate = false;
    this.container.addChild(spine);

    let text = null;
    const slot = TEXT_SLOT_BY_RIG[rigName];
    if (slot && spine.skeleton.findSlot(slot)) {
      // A Sprite from the label cache (coinLabel.js), at the rig's own size:
      // the spine is scaled to the cell and the label inherits that scale
      // inside the slot. Never rounded — it rides the landing.
      text = createCoinLabel();
      // spine.addSlotObject(slot, text);
      // In a holder: spine-pixi rewrites a slot object's transform every
      // frame, which wiped setCoinLabel's horizontal squeeze (Vladimir,
      // 2026-10-13 — same fix as wearCoinValue).
      const holder = new PIXI.Container();
      holder.name = "coin_value_holder";
      holder.addChild(text);
      spine.addSlotObject(slot, holder);
    }

    const entry = { spine, text };
    this.rigs.set(rigName, entry);
    return entry;
  }

  _scale(spine, rigName) {
    if (!this.cellHeight) return;
    // The same size as the static picture (HoldAndWinCell.artScale).
    const pay = this.active?.spine === spine ? this._currentSymbolPay : null;
    const category =
      (pay && Settings.SYMBOL_SCALE_OVERRIDES?.[pay]) ??
      Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT ??
      1;
    spine.scale.set((this.cellHeight / Settings.SYMBOL_HEIGHT) * category);
  }

  _setValue(entry, value) {
    const { text } = entry;
    if (!text) return;
    setCoinLabel(
      text,
      value > 0 ? formatToCustomLocale(value) : "",
      Settings.SYMBOL_HEIGHT * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
      // Settings.SYMBOL_HEIGHT * 0.9,
      Settings.SYMBOL_HEIGHT * COIN_LABEL_MAX_WIDTH_RATIO,
      // A revealed jackpot coin keeps its tier's colours (revealJackpotValue).
      entry.styleName || "coinValue",
    );
  }

  _hide(entry) {
    entry.spine.state.clearTracks();
    entry.spine.visible = false;
    entry.spine.autoUpdate = false;
  }
}
